import { useEffect, useRef, useState, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "./hooks/useTheme";
import { GoogleOAuthProvider } from "@react-oauth/google";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthCallback from "./pages/AuthCallback";
import DemoEnquiry from "./pages/DemoEnquiry";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import RefundPolicy from "./pages/RefundPolicy";
import UserHome from "./pages/user/UserHome";
import UserMenu from "./pages/user/UserMenu";
import UserWallet from "./pages/user/UserWallet";
import UserOrders from "./pages/user/UserOrders";
import UserCart from "./pages/user/UserCart";
import OrderTracking from "./pages/user/OrderTracking";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminMenu from "./pages/admin/AdminMenu";
import AdminPlans from "./pages/admin/AdminPlans";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminTaxes from "./pages/admin/AdminTaxes";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminOrderSerials from "./pages/admin/AdminOrderSerials";
import AdminBackup from "./pages/admin/AdminBackup";
import AdminCustomers from "./pages/admin/AdminCustomers";
import AdminWalletRecharges from "./pages/admin/AdminWalletRecharges";
import AdminReports from "./pages/admin/AdminReports";
import AdminRoles from "./pages/admin/AdminRoles";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminProfile from "./pages/admin/AdminProfile";
import AdminSuperfoodBlog from "./pages/admin/AdminSuperfoodBlog";
import AdminKitchens from "./pages/admin/AdminKitchens";
import AdminRefunds from "./pages/admin/AdminRefunds";
import AdminKitchenMenus from "./pages/admin/AdminKitchenMenus";
import AdminEnquiries from "./pages/admin/AdminEnquiries";
import AdminDiscount from "./pages/admin/AdminDiscount";
import AdminApiKeys from "./pages/admin/AdminApiKeys";
import RiderDashboard from "./pages/admin/RiderDashboard";
import UserProfile from "./pages/user/UserProfile";
import UserNotifications from "./pages/user/UserNotifications";
import AdminSegments from "./pages/admin/AdminSegments";
import AdminCobranding from "./pages/admin/AdminCobranding";
import AdminReferrals from "./pages/admin/AdminReferrals";
import AdminRecipes from "./pages/admin/AdminRecipes";
import AdminRecipeCategories from "./pages/admin/AdminRecipeCategories";
import AdminRecipeTags from "./pages/admin/AdminRecipeTags";
import { AdminRecipeOperationalUnits, AdminRecipeMetricUnits } from "./pages/admin/AdminRecipeLookups";
import AdminHomeBanners from "./pages/admin/AdminHomeBanners";
import AdminRecipePreview from "./pages/admin/AdminRecipePreview";
import { CookRecipes, CookRecipeDetail, CookFavorites, CookProfile } from "./pages/cook/CookPages";
import SegmentAdminDashboard from "./pages/segment_admin/SegmentAdminDashboard";
import DesktopShell from "./components/DesktopShell";
import AppUpdateGate from "./components/AppUpdateGate";
import ImpersonationBanner from "./components/ImpersonationBanner";
import IOSInstallHint from "./components/IOSInstallHint";
import OnboardingWizard from "./components/OnboardingWizard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Cart Context
export const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};

// Currency Context
export const CurrencyContext = createContext(null);

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) return { symbol: "₹", code: "INR" };
  return context;
};

// Disable Capacitor's native HTTP fetch interception.
// Capacitor 7 patches window.fetch to route through native HTTP, which consumes
// the response body before JS can read it ("body stream already read" error).
// Restore the original browser fetch if Capacitor has patched it.
if (window.CapacitorWebFetch || (window.Capacitor && window._originalFetch)) {
  // Capacitor stores the original fetch as window.CapacitorWebFetch or similar
  if (window.CapacitorWebFetch) window.fetch = window.CapacitorWebFetch;
}
// Alternative: if Capacitor hasn't exposed the original, create XHR-based fetch
if (window.Capacitor && !window._fetchRestored) {
  const nativeFetch = window.fetch;
  window.fetch = function(url, opts = {}) {
    // For same-origin API calls, use XHR to bypass Capacitor interception
    if (typeof url === 'string' && (url.includes('/api/') || url.startsWith(process.env.REACT_APP_BACKEND_URL))) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(opts.method || 'GET', url, true);
        if (opts.headers) {
          Object.entries(opts.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        }
        if (opts.credentials === 'include') xhr.withCredentials = true;
        xhr.onload = () => {
          resolve({
            ok: xhr.status >= 200 && xhr.status < 300,
            status: xhr.status,
            statusText: xhr.statusText,
            headers: { get: (h) => xhr.getResponseHeader(h) },
            text: () => Promise.resolve(xhr.responseText),
            json: () => Promise.resolve(JSON.parse(xhr.responseText)),
            blob: () => Promise.resolve(new Blob([xhr.response])),
            clone: function() { return this; },
          });
        };
        xhr.onerror = () => reject(new TypeError('Network request failed'));
        xhr.send(opts.body || null);
      });
    }
    // For non-API calls (Google OAuth, etc.), use native fetch
    return nativeFetch.call(window, url, opts);
  };
  window._fetchRestored = true;
}

// Web (non-Capacitor) impersonation header injector.
// Wraps window.fetch once so every /api/* request carries the imp token when
// admin is in "View as user" mode. Idempotent — guarded by _impFetchPatched.
if (!window._impFetchPatched) {
  const _originalFetch = window.fetch.bind(window);
  window.fetch = function(input, init = {}) {
    const url = typeof input === 'string' ? input : (input && input.url);
    if (url && (url.includes('/api/') || url.startsWith(process.env.REACT_APP_BACKEND_URL || ''))) {
      try {
        const tok = window.sessionStorage.getItem('imp_token');
        if (tok) {
          init = init || {};
          const h = new Headers(init.headers || {});
          if (!h.has('X-Impersonation-Token')) h.set('X-Impersonation-Token', tok);
          init.headers = h;
        }
      } catch (_) { /* ignore */ }
    }
    return _originalFetch(input, init);
  };
  window._impFetchPatched = true;
}

// Portrait orientation lock — best-effort for both Capacitor & web.
// Modern browsers ignore screen.orientation.lock() on desktop; on Capacitor WebView
// it actually locks. The CSS fallback (below in index.css via media query) hides
// the UI behind a "Rotate your phone" overlay on small landscape viewports.
if (typeof window !== "undefined" && !window._orientationLockTried) {
  try {
    if (window.screen && window.screen.orientation && typeof window.screen.orientation.lock === "function") {
      window.screen.orientation.lock("portrait").catch(() => { /* ignore on unsupported platforms */ });
    }
  } catch (_) { /* ignore */ }
  window._orientationLockTried = true;
}

function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState({ symbol: "₹", code: "INR", logoUrl: "", homeLogoUrl: "" });

  useEffect(() => {
    fetch(`${API}/settings`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (data) setCurrency({ symbol: data.currency_symbol, code: data.currency_code, logoUrl: data.logo_url || "", homeLogoUrl: data.home_logo_url || "" });
      })
      .catch(() => {});
  }, []);

  return (
    <CurrencyContext.Provider value={currency}>
      {children}
    </CurrencyContext.Provider>
  );
}

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Skip checkAuth if session_token is in URL (deep link / server-side OAuth flow).
    // LandingPage will handle the token exchange and set user state.
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("session_token")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout failed:", error);
    }
    setUser(null);
    // Intentionally keep `sm_returning_user` — the login screen uses it to
    // show a warm "Continue as {name}" pill on the next visit. Cleared only
    // if the user explicitly hits the tiny "Not you?" affordance below the
    // pill (see LandingPage).
  };

  const googleLogin = async (credential, directUserData) => {
    // If called with directUserData (from useGoogleLogin flow), just set user
    if (directUserData) {
      setUser(directUserData);
      return directUserData;
    }
    try {
      const response = await fetch(`${API}/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ credential }),
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return userData;
      }
      return null;
    } catch (error) {
      console.error("Google login failed:", error);
      return null;
    }
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  // Persist a lightweight "returning user" hint whenever we have a signed-in
  // user, so the next visit's login screen can greet them by first name.
  useEffect(() => {
    if (!user) return;
    try {
      const first = (user.name || "").trim().split(/\s+/)[0] || "";
      if (!first) return;
      localStorage.setItem(
        "sm_returning_user",
        JSON.stringify({ first_name: first, picture: user.picture || "" })
      );
    } catch (_) { /* localStorage disabled — ignore */ }
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, googleLogin, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

// Cart Provider
function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem("supermorsel_cart");
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem("supermorsel_cart", JSON.stringify(cart));
  }, [cart]);

  // Generate unique cart key based on item + selected options
  const getCartKey = (item) => {
    const varKey = (item.selected_variations || [])
      .map((v) => `${v.variation_id}:${v.option_id}`)
      .sort()
      .join("|");
    const addonKey = (item.selected_addons || [])
      .map((a) => a.addon_id)
      .sort()
      .join("|");
    return `${item.item_id}__${varKey}__${addonKey}`;
  };

  const addToCart = (item) => {
    const cartKey = getCartKey(item);
    setCart((prev) => {
      const existing = prev.find((i) => i.cart_key === cartKey);
      if (existing) {
        return prev.map((i) =>
          i.cart_key === cartKey ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { ...item, cart_key: cartKey, quantity: 1 }];
    });
  };

  const removeFromCart = (cartKey) => {
    setCart((prev) => prev.filter((i) => i.cart_key !== cartKey));
  };

  const updateQuantity = (cartKey, quantity) => {
    if (quantity <= 0) {
      removeFromCart(cartKey);
      return;
    }
    setCart((prev) =>
      prev.map((i) => (i.cart_key === cartKey ? { ...i, quantity } : i))
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const getTotal = () => {
    return cart.reduce((sum, item) => {
      let itemPrice = item.price || 0;
      // Add variation price adjustments
      (item.selected_variations || []).forEach((v) => {
        itemPrice += v.price_adjustment || 0;
      });
      // Add addon prices
      (item.selected_addons || []).forEach((a) => {
        itemPrice += a.price || 0;
      });
      return sum + itemPrice * item.quantity;
    }, 0);
  };

  const getItemCount = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  const getCartQuantityForItem = (itemId) => {
    return cart
      .filter((i) => i.item_id === itemId)
      .reduce((sum, i) => sum + i.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotal,
        getItemCount,
        getCartQuantityForItem,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

// Protected Route
function ProtectedRoute({ children, adminOnly = false, allowManager = false, segmentAdminOnly = false, cookOnly = false, allowNutritionist = false, allowChef = false }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  let allowedRoles;
  if (segmentAdminOnly) allowedRoles = ["segment_admin"];
  else if (cookOnly) allowedRoles = ["cook"];
  else allowedRoles = ["admin"];
  if (allowManager && !segmentAdminOnly && !cookOnly) { allowedRoles.push("manager"); allowedRoles.push("rider"); allowedRoles.push("chef"); }
  if (allowChef && !allowedRoles.includes("chef")) allowedRoles.push("chef");
  if (allowNutritionist && !cookOnly) allowedRoles.push("nutritionist");

  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate("/", { replace: true });
      } else if ((adminOnly || segmentAdminOnly || cookOnly) && !allowedRoles.includes(user.role)) {
        // Steer to their natural landing page
        if (user.role === "segment_admin") navigate("/sa", { replace: true });
        else if (user.role === "cook") navigate("/cook", { replace: true });
        else if (user.role === "nutritionist") navigate("/admin/recipes", { replace: true });
        else navigate("/home", { replace: true });
      }
    }
  }, [user, loading, navigate, adminOnly, segmentAdminOnly, cookOnly]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;
  if ((adminOnly || segmentAdminOnly || cookOnly) && !allowedRoles.includes(user.role)) return null;

  return children;
}

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id - do this synchronously during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/demo" element={<DemoEnquiry />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* User Routes */}
      <Route
        path="/home"
        element={
          <ProtectedRoute>
            <UserHome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/menu"
        element={
          <ProtectedRoute>
            <UserMenu />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wallet"
        element={
          <ProtectedRoute>
            <UserWallet />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <UserOrders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cart"
        element={
          <ProtectedRoute>
            <UserCart />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <UserProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/:orderId"
        element={
          <ProtectedRoute>
            <OrderTracking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <UserNotifications />
          </ProtectedRoute>
        }
      />
      
      {/* Admin Routes - Manager accessible */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly allowManager>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/menu"
        element={
          <ProtectedRoute adminOnly allowManager>
            <AdminMenu />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/orders"
        element={
          <ProtectedRoute adminOnly allowManager>
            <AdminOrders />
          </ProtectedRoute>
        }
      />
      
      {/* Admin-only Routes */}
      <Route
        path="/admin/plans"
        element={
          <ProtectedRoute adminOnly>
            <AdminPlans />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/taxes"
        element={
          <ProtectedRoute adminOnly>
            <AdminTaxes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute adminOnly>
            <AdminSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/categories"
        element={
          <ProtectedRoute adminOnly>
            <AdminCategories />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/order-serials"
        element={
          <ProtectedRoute adminOnly>
            <AdminOrderSerials />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/backup"
        element={
          <ProtectedRoute adminOnly>
            <AdminBackup />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/customers"
        element={
          <ProtectedRoute adminOnly>
            <AdminCustomers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/recharges"
        element={
          <ProtectedRoute adminOnly>
            <AdminWalletRecharges />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reports"
        element={
          <ProtectedRoute adminOnly>
            <AdminReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/roles"
        element={
          <ProtectedRoute adminOnly>
            <AdminRoles />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reviews"
        element={
          <ProtectedRoute adminOnly>
            <AdminReviews />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/superfood-blog"
        element={
          <ProtectedRoute adminOnly>
            <AdminSuperfoodBlog />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/kitchens"
        element={
          <ProtectedRoute adminOnly>
            <AdminKitchens />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/kitchen-menus"
        element={
          <ProtectedRoute adminOnly>
            <AdminKitchenMenus />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/refunds"
        element={
          <ProtectedRoute adminOnly>
            <AdminRefunds />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/enquiries"
        element={
          <ProtectedRoute adminOnly>
            <AdminEnquiries />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/discount"
        element={
          <ProtectedRoute adminOnly>
            <AdminDiscount />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/api-keys"
        element={
          <ProtectedRoute adminOnly>
            <AdminApiKeys />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/segments"
        element={
          <ProtectedRoute adminOnly>
            <AdminSegments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cobranding"
        element={
          <ProtectedRoute adminOnly>
            <AdminCobranding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/referrals"
        element={
          <ProtectedRoute adminOnly>
            <AdminReferrals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/recipes"
        element={
          <ProtectedRoute adminOnly allowNutritionist allowChef allowManager>
            <AdminRecipes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/recipe-categories"
        element={
          <ProtectedRoute adminOnly allowNutritionist>
            <AdminRecipeCategories />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/recipe-tags"
        element={
          <ProtectedRoute adminOnly allowNutritionist>
            <AdminRecipeTags />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/recipe-operational-units"
        element={
          <ProtectedRoute adminOnly allowNutritionist>
            <AdminRecipeOperationalUnits />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/recipe-metric-units"
        element={
          <ProtectedRoute adminOnly allowNutritionist>
            <AdminRecipeMetricUnits />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/home-banners"
        element={
          <ProtectedRoute adminOnly>
            <AdminHomeBanners />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/recipes/:recipe_id/preview"
        element={
          <ProtectedRoute adminOnly allowNutritionist allowChef allowManager>
            <AdminRecipePreview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cook"
        element={
          <ProtectedRoute cookOnly>
            <CookRecipes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cook/recipes/:recipe_id"
        element={
          <ProtectedRoute cookOnly>
            <CookRecipeDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cook/favorites"
        element={
          <ProtectedRoute cookOnly>
            <CookFavorites />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cook/profile"
        element={
          <ProtectedRoute cookOnly>
            <CookProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sa"
        element={
          <ProtectedRoute segmentAdminOnly>
            <SegmentAdminDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/profile"
        element={
          <ProtectedRoute adminOnly allowManager allowNutritionist>
            <AdminProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/deliveries"
        element={
          <ProtectedRoute adminOnly allowManager>
            <RiderDashboard />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <BrowserRouter>
        <AuthProvider>
          <ThemeProvider>
            <CurrencyProvider>
              <CartProvider>
                <Toaster position="top-center" richColors />
                <AppUpdateGate />
                <ImpersonationBanner />
                <IOSInstallHint />
                <OnboardingWizard />
                <DesktopShell>
                  <AppRouter />
                </DesktopShell>
              </CartProvider>
            </CurrencyProvider>
          </ThemeProvider>
        </AuthProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
